<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button class="btn btn-primary" @click="handleCreate">
                <i class="ri-add-line"></i> 新增短網址
              </button>
      </div>
    </div>

    <div class="page-container">
    <div
      class="short-url-top-row"
      :class="{ 'short-url-top-row--single': hideNotice }"
    >
      <div class="short-url-intro">
        <div class="short-url-intro-icon" aria-hidden="true">
          <i class="ri-booklet-line"></i>
        </div>
        <div class="short-url-intro-content">
          <strong>功能說明</strong>
          <p>
            LINE 短網址用於對外行銷或活動連結：您設定一個<strong>目標網址</strong>後，系統會產生可分享的短連結。
            使用者點擊後會先經由 <strong>LIFF</strong> 流程辨識 LINE 身分、必要時引導加入官方帳號好友，再導向目標頁面。
          </p>
          <ul>
            <li>列表中的<strong>點擊次數</strong>、<strong>加好友次數</strong>可觀察連結成效；點「詳情」可查看統計與點擊紀錄。</li>
            <li>新增或編輯時可設定來源類型、有效期限、自動貼標等，方便後續會員來源與標籤分析。</li>
          </ul>
        </div>
      </div>

      <!-- 設定提醒（與功能說明並排，右側） -->
      <div v-if="!hideNotice" class="config-notice">
        <div class="config-notice-icon">
          <i class="ri-information-line"></i>
        </div>
        <div class="config-notice-content">
          <strong>短網址無法正常運作？</strong>
          請確認 LINE 官方帳號已正確設定以下兩項：
          <ul>
            <li>
              <strong>LIFF Endpoint URL</strong>（LINE Developers Console → LIFF 設定）
              <br>
              <span class="config-notice-hint">格式範例：<code>https://liff.lockline.io/lockline/lineliff/{Messaging Channel ID}</code></span>
            </li>
            <li>
              <strong>LINE Login Callback URL</strong>（LINE Developers Console → LINE Login 設定）
              <br>
              <span class="config-notice-hint">格式範例：<code>https://liff.lockline.io/lockline/linelogin/{Messaging Channel ID}</code></span>
            </li>
          </ul>
          <span class="config-notice-link">
            可於
            <router-link :to="`/client/line/config`">LINE 頻道設定</router-link>
            頁面確認 Messaging Channel ID。
          </span>
        </div>
        <button class="config-notice-close" @click="hideNotice = true" title="關閉提醒">
          <i class="ri-close-line"></i>
        </button>
      </div>
    </div>

    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchKeyword"
          type="text"
          class="form-control"
          placeholder="搜尋名稱、短代碼..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <button class="btn btn-secondary" @click="loadShortUrls">
        <i class="ri-refresh-line"></i> 重新整理
      </button>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="loadShortUrls">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>名稱</th>
            <th>完整短網址</th>
            <th>目標網址</th>
            <th>點擊次數</th>
            <th>加好友次數</th>
            <th>狀態</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="shortUrls.length === 0">
            <td colspan="8" class="empty-state">
              尚無短網址資料
            </td>
          </tr>
          <tr v-for="item in shortUrls" :key="item.id">
            <td>{{ item.urlName || '-' }}</td>
            <td>
              <div class="short-url-cell">
                <code class="short-url-code" :title="item.shortUrl || item.fullShortUrl">
                  {{ item.shortUrl || item.fullShortUrl || '-' }}
                </code>
                <button
                  v-if="item.shortUrl || item.fullShortUrl"
                  class="btn btn-xs btn-copy"
                  @click.stop="copyUrl(item.shortUrl || item.fullShortUrl)"
                  title="複製短網址"
                >
                  <i class="ri-file-copy-line"></i>
                </button>
              </div>
            </td>
            <td class="url-cell" :title="item.targetUrl">{{ item.targetUrl || '-' }}</td>
            <td>{{ item.clickCount || 0 }}</td>
            <td>{{ item.addFriendCount || 0 }}</td>
            <td>
              <span :class="getStatusBadgeClass(item.isActive)">
                {{ item.isActive ? '啟用' : '停用' }}
              </span>
            </td>
            <td>{{ formatDate(item.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleDetail(item.id)"
                >
                  <i class="ri-eye-line"></i> 詳情
                </button>
                <button
                  class="btn btn-sm btn-primary"
                  @click="handleEdit(item.id)"
                >
                  <i class="ri-edit-line"></i> 編輯
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(item.id)"
                >
                  <i class="ri-delete-bin-line"></i> 刪除
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="pagination.total > 0"
      :page="pagination.page"
      :total-pages="pagination.totalPages"
      :total="pagination.total"
      @change="changePage"
    />
    </div>
  </div>
</template>

<script>
import { usePageTitle } from '@/composables/usePageTitle.js';
import lineShortUrlApi from '@/api/modules/lineShortUrl.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'LineShortUrlList',
  setup() {
    const { pageTitle } = usePageTitle('LINE 短網址管理');
    return { pageTitle };
  },
  data() {
    return {
      hideNotice: false,
      loading: false,
      error: null,
      searchKeyword: '',
      shortUrls: [],
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0
      },
      searchTimeout: null
    };
  },
  mounted() {
    this.loadShortUrls();
  },
  methods: {
    async loadShortUrls() {
      this.loading = true;
      this.error = null;
      try {
        const response = await lineShortUrlApi.list({
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
          search: this.searchKeyword
        });
        
        if (response && response.success) {
          this.shortUrls = response.data?.items || response.data || [];
          const pag = response.data?.pagination;
          if (pag) {
            this.pagination = {
              ...this.pagination,
              page: pag.page ?? this.pagination.page,
              total: pag.totalItems ?? pag.total ?? 0,
              totalPages: pag.totalPages ?? this.pagination.totalPages
            };
          }
        }
      } catch (error) {
        console.error('載入短網址列表失敗:', error);
        this.error = error?.response?.data?.message || error?.message || '載入失敗，請稍後再試';
        this.$message?.error(this.error);
      } finally {
        this.loading = false;
      }
    },

    changePage(page) {
      this.pagination.page = page;
      this.loadShortUrls();
    },

    handleSearch() {
      // 使用防抖處理搜尋
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.loadShortUrls();
      }, 500);
    },

    handleCreate() {
      this.$router.push({ name: 'ClientCreateLineShortUrl' });
    },

    handleDetail(id) {
      this.$router.push({ name: 'ClientLineShortUrlDetail', params: { id } });
    },

    handleEdit(id) {
      this.$router.push({ name: 'ClientEditLineShortUrl', params: { id } });
    },

    async handleDelete(id) {
      if (!confirm('確定要刪除此短網址？')) {
        return;
      }
      try {
        await lineShortUrlApi.delete(id);
        this.$message?.success('刪除成功');
        this.loadShortUrls();
      } catch (error) {
        console.error('刪除失敗:', error);
        this.$message?.error('刪除失敗，請稍後再試');
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getStatusBadgeClass(isActive) {
      return isActive ? 'badge badge-success' : 'badge badge-secondary';
    },

    async copyUrl(url) {
      if (!url) return;
      try {
        await navigator.clipboard.writeText(url);
        await showSuccess('已複製', url, 1500);
      } catch {
        await showError('複製失敗', '請手動複製網址');
      }
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

.short-url-top-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 16px;
  margin-bottom: 16px;
  align-items: stretch;
}

.short-url-top-row--single {
  grid-template-columns: 1fr;
}

@media (max-width: 1100px) {
  .short-url-top-row:not(.short-url-top-row--single) {
    grid-template-columns: 1fr;
  }
}

.short-url-intro {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #e8f4ff;
  border: 1px solid #b3d7ff;
  border-left: 4px solid #1890ff;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 0;
  font-size: 13px;
  color: #1a3a52;
  line-height: 1.65;
  min-width: 0;
}

.short-url-intro-icon {
  flex-shrink: 0;
  font-size: 20px;
  color: #1890ff;
  margin-top: 1px;
}

.short-url-intro-content {
  flex: 1;
  min-width: 0;
}

.short-url-intro-content > strong {
  display: block;
  font-size: 14px;
  color: #0d3d66;
  margin-bottom: 8px;
}

.short-url-intro-content p {
  margin: 0 0 10px 0;
}

.short-url-intro-content ul {
  margin: 0;
  padding-left: 18px;
}

.short-url-intro-content li {
  margin-bottom: 6px;
}

.short-url-intro-content li:last-child {
  margin-bottom: 0;
}

.url-cell {
  max-width: 260px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.table-wrapper table {
  min-width: 1200px;
}

code {
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #e83e8c;
}

.short-url-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  max-width: 280px;
}

.short-url-code {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 230px;
}

.btn-xs {
  padding: 2px 6px;
  font-size: 12px;
  line-height: 1.4;
  border-radius: 3px;
  flex-shrink: 0;
}

.btn-copy {
  background: #f0f0f0;
  color: #555;
  border: 1px solid #ddd;
  cursor: pointer;
  transition: background 0.2s;
}

.btn-copy:hover {
  background: #e0e0e0;
  color: #333;
}

/* 設定提醒 Banner */
.config-notice {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-left: 4px solid #faad14;
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 0;
  font-size: 13px;
  color: #5a4a00;
  position: relative;
  min-width: 0;
}

.config-notice-icon {
  flex-shrink: 0;
  font-size: 18px;
  color: #faad14;
  margin-top: 1px;
}

.config-notice-content {
  flex: 1;
  line-height: 1.6;
}

.config-notice-content strong {
  color: #3d3000;
}

.config-notice-content ul {
  margin: 8px 0 6px 0;
  padding-left: 18px;
}

.config-notice-content li {
  margin-bottom: 6px;
}

.config-notice-hint {
  font-size: 12px;
  color: #876800;
}

.config-notice-hint code {
  background: #fff3cc;
  padding: 1px 5px;
  border-radius: 3px;
  font-family: monospace;
  font-size: 11px;
  border: 1px solid #ffe58f;
}

.config-notice-link {
  font-size: 12px;
  color: #876800;
}

.config-notice-link a {
  color: #1890ff;
  text-decoration: none;
}

.config-notice-link a:hover {
  text-decoration: underline;
}

.config-notice-close {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #999;
  font-size: 16px;
  padding: 0 0 0 8px;
  line-height: 1;
  transition: color 0.2s;
}

.config-notice-close:hover {
  color: #666;
}
</style>
